// The plan in time order, which is the shape a phone can read.
//
// A seven-stage grid is wider than a phone screen, and scrolling sideways
// through six stages nobody chose to see is a way to look at a day rather than
// a way to follow one. The grid stays available beside this.
//
// Every block is a real element with a real label. A lock is a button carrying
// aria-pressed and a word, not a color.

import { VENUE_TIMEZONE } from '../../engine/time.ts';
import type { CSSProperties } from 'react';

export interface AgendaBlock {
  id: string;
  label: string;
  place: string;
  startTs: number;
  endTs: number;
  kind: 'set' | 'rest' | 'walk' | 'meet';
  locked?: boolean;
  onToggleLock?: () => void;
  /** The stage's color, so a row is identifiable without reading the venue. */
  color?: string;
  color2?: string;
}

const KIND_LABEL: Record<AgendaBlock['kind'], string> = {
  meet: 'Meet here',
  walk: 'Travel time',
  rest: 'Break',
  set: 'Music',
};

export function ScheduleAgenda({
  blocks,
  timeZone = VENUE_TIMEZONE,
}: {
  blocks: AgendaBlock[];
  timeZone?: string;
}) {
  const format = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  });

  if (blocks.length === 0) {
    return <p className="muted">Nothing planned for this day yet.</p>;
  }

  return (
    <ol className="schedule-agenda" aria-label="Schedule in time order">
      {blocks.map((b) => (
        <li key={b.id} style={{ '--stage': b.color ?? 'var(--accent)', '--stage2': b.color2 ?? 'var(--accent-cool)' } as CSSProperties}>
          <div>
            <time dateTime={new Date(b.startTs).toISOString()}>{format.format(b.startTs)}</time>
            {' to '}
            <time dateTime={new Date(b.endTs).toISOString()}>{format.format(b.endTs)}</time>
          </div>
          {b.onToggleLock ? (
            <button type="button" aria-pressed={Boolean(b.locked)} onClick={b.onToggleLock}>
              {b.label}
              {b.locked ? ' · locked' : ''}
            </button>
          ) : (
            <strong>{b.label}</strong>
          )}
          <div className="muted">
            {b.place} · {KIND_LABEL[b.kind]}
          </div>
        </li>
      ))}
    </ol>
  );
}
